import { useId, useMemo } from "react";

import type { AccessRole } from "@/modules/dashboard/settings/types/access.types";
import { ColocaCombobox } from "@/shared/components/forms/ColocaCombobox";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

import { USERS_FILTER_ALL, USERS_SEARCH_MAX_LENGTH } from "../constants/users.constants";

export interface UsersToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  isTermTooShort: boolean;
  roleId: string;
  onRoleChange: (value: string) => void;
  roles: readonly AccessRole[];
  rolesUnavailable: boolean;
}

/**
 * Search plus the role picker. **No status control** — the tile strip above is the status filter, so putting
 * one here would restate the same axis twice.
 *
 * Nor is there an organization filter: `QueryUsersDto` has no `organization_id`, `forbidNonWhitelisted` turns
 * an invented param into a 400, and a client-side approximation over 25 rows produces confidently wrong
 * answers while `total` and the pagination keep describing the unfiltered set. A disabled placeholder is not
 * offered either — a greyed-out control is a promise.
 */
export function UsersToolbar({
  query,
  onQueryChange,
  isTermTooShort,
  roleId,
  onRoleChange,
  roles,
  rolesUnavailable,
}: UsersToolbarProps) {
  const { t } = useTranslations("users");
  const roleFieldId = useId();

  const roleOptions = useMemo(
    () => [
      { value: USERS_FILTER_ALL, label: t("filter_role_all") },
      ...roles.map((role) => ({ value: role.id, label: role.name })),
    ],
    [roles, t],
  );

  return (
    <ColocaSurface padding="md">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex w-full flex-col gap-1.5 lg:max-w-sm">
          <ColocaSearchInput
            value={query}
            onValueChange={onQueryChange}
            placeholder={t("search_placeholder")}
            maxLength={USERS_SEARCH_MAX_LENGTH}
            aria-label={t("search_placeholder")}
          />
          {isTermTooShort && <span className="text-xs text-muted-foreground">{t("search_min_hint")}</span>}
        </div>

        {/* Absent, not disabled, when the catalog could not be read: an inert control implies it might work. */}
        {!rolesUnavailable && (
          <div className="flex w-full items-center gap-2 lg:w-auto">
            <label htmlFor={roleFieldId} className="shrink-0 text-xs text-muted-foreground">
              {t("filter_role_label")}
            </label>
            <ColocaCombobox
              id={roleFieldId}
              value={roleId}
              onValueChange={(next) => onRoleChange(next ?? USERS_FILTER_ALL)}
              options={roleOptions}
              placeholder={t("filter_role_all")}
              className="w-full sm:w-64"
            />
          </div>
        )}
      </div>
    </ColocaSurface>
  );
}
